import { inject, Injectable } from '@angular/core';
import { gql } from '@apollo/client/core';
import { AppConfig } from '@app/core/app.config';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import {
  BiReport,
  CreateBiReportInput,
  ReportType,
  UpdateBiReportInput
} from '@app/core/model/entities/bi-report/bi-report';
import { SelectParams } from '@app/shared/grid/datasource-utils';
import ApiService from '@services/api.service';
import { plainToInstance } from 'class-transformer';
import { EMPTY, expand, Observable, Subject } from 'rxjs';
import { map, reduce, tap } from 'rxjs/operators';

@Injectable()
export class BiReportsService {

  private readonly biReportFragment = gql`
    fragment BiReport on BiReport {
      id
      name
      organizationId
      reportType
      reportId
      entityType
      view
      assetTypeId
      properties
      computedProperties
    }
  `;

  private biReportAdded = new Subject<BiReport>();
  private biReportUpdated = new Subject<BiReport>();
  private biReportsDeleted = new Subject<BiReport[]>();

  private readonly apiService = inject(ApiService);
  private readonly appConfig = inject(AppConfig);

  /**
   * Emits the new BI report after it was created.
   * @return Observable emitting the new BiReport.
   */
  public get biReportAdded$(): Observable<BiReport> {
    return this.biReportAdded.asObservable();
  }

  /**
   * Emits the updated BI report after it was updated.
   * @return Observable emitting the updated BiReport.
   */
  public get biReportUpdated$(): Observable<BiReport> {
    return this.biReportUpdated.asObservable();
  }

  /**
   * Emits all the BI reports that were deleted.
   * @return Observable emitting an array of deleted BI reports.
   */
  public get biReportsDeleted$(): Observable<BiReport[]> {
    return this.biReportsDeleted.asObservable();
  }

  /**
   * Loads all BI reports pages based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch BI reports based on the provided select parameters.
   * The select parameters include sorting, filtering, and grouping.
   *
   * @param organizationId The ID of the organization for which BI reports are to be fetched.
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, and locale settings.
   * @return An Observable emitting an array of BI Reports objects.
   */
  public loadAllBiReports(organizationId: string, selectParams: SelectParams = {}): Observable<BiReport[]> {
    const pageSize = this.appConfig.MAX_REQUEST_PAGE_SIZE;
    const params = {...selectParams, start: 0, count: pageSize};
    return this.loadBiReportsPage(organizationId, params)
      .pipe(
        expand((biReports, i) => {
          if (biReports.length < pageSize) {
            return EMPTY;
          }
          params.start = (i + 1) * pageSize;
          return this.loadBiReportsPage(organizationId, params);
        }),
        reduce((all, page) => {
          all.push(...page);
          return all;
        }, [] as BiReport[])
      );
  }

  /**
   * Checks if there is a paginated BI report for the specified organization.
   * @param organizationId - The ID of the organization to check for paginated BI reports.
   * @param entityType - The type of entity to check for paginated BI reports.
   * @return An observable that emits a boolean indicating whether a paginated BI report exists for the specified organization.
   */
  public hasPaginatedBiReport(organizationId: string, entityType: EntityTypeEnum): Observable<boolean> {
    const query = gql`
      query IsValueTaken($entityType: EntityType!, $value: Object!, $exclude: String!, $filter: Object!) {
        isValueTaken(entityType: $entityType, value: $value, exclude: $exclude, filter: $filter )
      }
    `;
    const variables = {
      entityType: EntityTypeEnum.BI_REPORT,
      value: {['entityType']: entityType},
      exclude: '',
      filter: {
        organizationId: organizationId,
        reportType: ReportType.PAGINATED
      }
    };
    return this.apiService.query({query, variables}).pipe(map(data => data['isValueTaken'] as boolean));
  }

  /**
   * Loads a BI report page based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch BI reports based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping, pagination.
   *
   * @param organizationId The ID of the organization for which BI reports are to be fetched.
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, pagination, and locale settings.
   * @return An Observable emitting an array of BI Report objects.
   */
  private loadBiReportsPage(organizationId: string, selectParams: SelectParams): Observable<BiReport[]> {
    const query = gql`
      query BiReports($organizationId: String!, $selectParams: SelectParams!) {
        biReports(organizationId: $organizationId, selectParams: $selectParams) {
          ...BiReport
        }
      }
      ${this.biReportFragment}
    `;
    const variables = {
      organizationId: organizationId,
      selectParams: selectParams
    };

    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(BiReport, data['biReports'] as BiReport[])));
  }

  /**
   * Loads bi reports associated with a specific user.
   * @param userId ID of the user whose BI reports are to be retrieved.
   * @return An observable that emits an array of BI reports belonging to the specified user.
   */
  public loadUserBiReports(userId: number): Observable<BiReport[]> {
    const query = gql`
      query UserBiReports($userId: Int!) {
        userBiReports(userId: $userId) {
          ...BiReport
        }
      }
      ${this.biReportFragment}
    `;
    const variables = {userId};

    return this.apiService.query({query, variables})
      .pipe(
        map(data => plainToInstance(BiReport, data['userBiReports'] as BiReport[]))
      );
  }

  /**
   * Creates a BI report based on the provided input.
   * @param biReportInput - The input data required to create a BI report.
   * @return An observable that emits the created BI report.
   */
  public createBiReport(biReportInput: CreateBiReportInput): Observable<BiReport> {
    const mutation = gql`
      mutation CreateBiReport($biReportInput: CreateBiReportInput!) {
        createBiReport(biReportInput: $biReportInput) {
          ...BiReport
        }
      }
      ${this.biReportFragment}
    `;
    const variables = {biReportInput};

    return this.apiService.mutate({mutation, variables}).pipe(
      map(data => data['createBiReport'] as BiReport),
      tap(biReport => this.biReportAdded.next(biReport))
    );
  }

  /**
   * Updates a BI report using the provided input and returns the updated BI report.
   *
   * @param biReportInput - The input data required to update the BI report.
   * @return An observable containing the updated BI report.
   */
  public updateBiReport(biReportInput: UpdateBiReportInput): Observable<BiReport> {
    const mutation = gql`
      mutation UpdateBiReport($biReportInput: UpdateBiReportInput!) {
        updateBiReport(biReportInput: $biReportInput) {
          ...BiReport
        }
      }
      ${this.biReportFragment}
    `;
    const variables = {biReportInput};

    return this.apiService.mutate({mutation, variables}).pipe(
      map(data => data['updateBiReport'] as BiReport),
      tap(biReport => this.biReportUpdated.next(biReport))
    );
  }

  /**
   * Deletes a list of BI reports by their IDs.
   * @param biReports - An array of BI reports to be deleted.
   * @return {Observable<boolean>} An Observable that emits a boolean indicating whether the delete operation was successful.
   */
  public deleteBiReports(biReports: BiReport[]): Observable<boolean> {
    const mutation = gql`
      mutation DeleteBiReports($biReportIds: [String!]!) {
        deleteBiReports(ids: $biReportIds)
      }
    `;
    const variables = {biReportIds: biReports.map(biReport => biReport.id)};

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => data['deleteBiReports']),
        tap(() => this.biReportsDeleted.next(biReports))
      );
  }
}
